import type {
  CustomerModel,
  CreateCustomerRequest,
  UpdateCustomerRequest,
} from '../../shared/models';
import { HttpRepository } from './HttpRepository';

export class CustomerRepository {
  private httpRepository = new HttpRepository(import.meta.env.VITE_API_URL);

  private customerSearchPath(params: Record<string, string>): string {
    const search = new URLSearchParams(params);
    return `/customers?${search.toString()}`;
  }

  /**
   * Insere um novo cliente
   */
  async create(data: CreateCustomerRequest): Promise<CustomerModel | null> {
    try {
      const response = await this.httpRepository.post<CustomerModel | null>(
        '/customers',
        {
          ...data,
          created_at: new Date().toISOString(),
        }
      );

      return response.data;
    } catch {
      return null;
    }
  }

  /**
   * Atualiza um cliente existente
   */
  async update(id: number, data: UpdateCustomerRequest): Promise<CustomerModel | null> {
    try {
      const response = await this.httpRepository.put<CustomerModel | null>(
        `/customers/${id}`,
        {
          ...data,
          updated_at: new Date().toISOString(),
        }
      );

      return response.data;
    } catch {
      return null;
    }
  }

  /**
   * Busca um cliente por ID
   */
  async findById(id: number): Promise<CustomerModel | null> {
    try {
      const response = await this.httpRepository.get<CustomerModel | null>(`/customers/${id}`);

      return response.data;
    } catch {
      return null;
    }
  }

  /**
   * Busca todos os clientes
   */
  async findAll(): Promise<CustomerModel[]> {
    try {
      const response = await this.httpRepository.get<CustomerModel[] | null>('/customers');

      return response.data ?? [];
    } catch {
      return [];
    }
  }

  /**
   * Busca cliente por número de documento
   */
  async findByDocument(documentNum: string): Promise<CustomerModel | null> {
    try {
      const response = await this.httpRepository.get<CustomerModel | null>(
        this.customerSearchPath({ document_num: documentNum })
      );

      return response.data;
    } catch {
      return null;
    }
  }

  /**
   * Busca clientes por nome (busca parcial)
   */
  async findByName(name: string): Promise<CustomerModel[]> {
    try {
      const response = await this.httpRepository.get<CustomerModel[] | null>(
        this.customerSearchPath({ name })
      );

      return response.data ?? [];
    } catch {
      return [];
    }
  }

  /**
   * Busca clientes por cidade
   */
  async findByCity(city: string): Promise<CustomerModel[]> {
    try {
      const response = await this.httpRepository.get<CustomerModel[] | null>(
        this.customerSearchPath({ city })
      );

      return response.data ?? [];
    } catch {
      return [];
    }
  }

  /**
   * Busca clientes por estado
   */
  async findByState(state: string): Promise<CustomerModel[]> {
    try {
      const response = await this.httpRepository.get<CustomerModel[] | null>(
        this.customerSearchPath({ state })
      );

      return response.data ?? [];
    } catch {
      return [];
    }
  }

  /**
   * Busca clientes por telefone
   */
  async findByPhone(phone: string): Promise<CustomerModel[]> {
    try {
      const response = await this.httpRepository.get<CustomerModel[] | null>(
        this.customerSearchPath({ phone })
      );

      return response.data ?? [];
    } catch {
      return [];
    }
  }
}
